import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Button,
  Autocomplete,
  Box,
  IconButton,
  Paper,
} from "@mui/material";
import {
  AttachFile,
  Visibility,
  Download,
  Delete,
  AddCircle,
} from "@mui/icons-material";
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  fetchCategorias,
  fetchTareas,
  fetchObrasSociales,
  fetchConvenios,
} from "../../services/empleados.api";
import { useFileUpload } from "../../../../../../Hooks/useUploadFiles";
import { DownloadFiles } from "../../../../../../services/DownloadFiles";
import { UrlApi } from "../../../../../../services/apirest";
import axios from "axios";

import { useLegajosContext } from "../../../../context/LegajoTabsContext";
import UploadFiles2024 from "../../../../../../services/UploadFiles2024";
import moment from "moment";

export default function LaboralesForm({ isEditing }) {
  const { formData, updateFormData, errors, referenceData, ensureUuidArchivo } =
    useLegajosContext();

  const { puestos, sectores, sucursales, sindicatos, bancos } =
    referenceData;

  const fechaIngreso = formData?.datosPersonales?.fecha_ingreso;
  const antiguedadAnterior = formData?.datosLaborales?.antiguedad_anterior;
  const uuid_archivo = formData?.datosLaborales?.uuid_archivo;

  const datos = formData?.datosLaborales;

  // Calcular antigüedad actual (memoizado para evitar recalcular en cada render)
  const antiguedadCalculada = useMemo(() => {
    if (!fechaIngreso) return "Sin fecha de ingreso";

    const ingreso = moment(fechaIngreso);
    const hoy = moment();
    const aniosActuales = hoy.diff(ingreso, "years");

    // Sumar años de antigüedad anterior si existen
    const aniosAnteriores = parseInt(antiguedadAnterior || 0);
    const totalAnios = aniosActuales + aniosAnteriores;

    return `${totalAnios} año${totalAnios !== 1 ? "s" : ""}`;
  }, [fechaIngreso, antiguedadAnterior]);

  const [categorias, setCategorias] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [obrasSociales, setObrasSociales] = useState([]);
  const [convenios, setConvenios] = useState([]);
  useEffect(() => {
    const fetchDatosPorEmpresa = async () => {
      const sucursalId = datos?.sucursal_id;
      if (!sucursalId) return;

      const sucursalSelected = sucursales?.find((s) => s.id === sucursalId);

      if (sucursalSelected && sucursalSelected.empresa) {
        try {
          const params = {
            empresa_id: sucursalSelected.empresa,
            puesto_id: datos?.puesto_id || "",
            convenio_id: datos?.convenio_id || "",
          };

          const cats = await fetchCategorias(params);
          setCategorias(cats.data || []);

          const ts = await fetchTareas({
            empresa_id: sucursalSelected.empresa,
          });
          setTareas(ts.data || []);

          const oss = await fetchObrasSociales({
            empresa_id: sucursalSelected.empresa,
          });
          setObrasSociales(oss.data || []);

          const conv = await fetchConvenios();
          setConvenios(conv.data || []);
        } catch (error) {
          console.error("Error fetching company data:", error);
        }
      }
    };

    fetchDatosPorEmpresa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datos?.sucursal_id]);

  const { uploadFile, isUploading } = useFileUpload();
  const [archivos, setArchivos] = useState([]);

  const fetchArchivos = useCallback(async () => {
    if (!uuid_archivo) return;
    try {
      const { data } = await axios.get(
        `${UrlApi}/uploadfiles/todownload/${uuid_archivo}`,
      );
      setArchivos(data);
    } catch (error) {
      console.error("Error fetching archivos:", error);
    }
  }, [uuid_archivo]);

  useEffect(() => {
    if (uuid_archivo) {
      fetchArchivos();
    }
  }, [uuid_archivo, fetchArchivos]);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const result = await uploadFile(files, uuid_archivo);

    fetchArchivos();
  };

  const handleViewFile = async (archivo) => {
    const name = `${archivo.id_archivo}-${archivo.nombre}`;
    const endpoint =
      archivo.extension === "application/pdf" ? "uploadspdf" : "uploads";
    try {
      const response = await fetch(`${UrlApi}/uploadfiles/${endpoint}/${name}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noreferrer");
    } catch (error) {
      console.error("Error viewing file:", error);
    }
  };

  const handleDeleteFile = async (archivo) => {
    if (window.confirm(`¿Seguro de borrar el archivo ${archivo.nombre}?`)) {
      try {
        await axios.delete(`${UrlApi}/uploadfiles/todownload/${archivo.id}`);
        fetchArchivos();
      } catch (error) {
        console.error("Error deleting file:", error);
      }
    }
  };
  useEffect(() => {
    if (!uuid_archivo) {
      ensureUuidArchivo();
    }
  }, [uuid_archivo, ensureUuidArchivo]);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Autocomplete
            fullWidth
            disabled={isEditing}
            options={sucursales || []}
            getOptionKey={(option) => option.id}
            getOptionLabel={(option) => option.nombre || ""}
            value={sucursales?.find((s) => s.id === datos.sucursal_id) || null}
            onChange={(event, newValue) => {
              updateFormData("datosLaborales", {
                target: {
                  name: "sucursal_id",
                  value: newValue ? newValue.id : null,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sucursal"
                variant="standard"
                size="small"
                margin="dense"
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors?.sucursal_id}
                helperText={errors?.sucursal_id}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <FormControl
            fullWidth
            required
            variant="standard"
            size="small"
            margin="dense"
            inputProps={{ readOnly: isEditing }}
            InputLabelProps={{ shrink: true }}
            error={!!errors?.sector_id}
          >
            <InputLabel shrink id="sector-label">
              Sector
            </InputLabel>
            <Select
              labelId="sector-label"
              id="sector_id"
              name="sector_id"
              inputLabelProps={{ shrink: true }}
              value={datos.sector_id}
              label="Sector"
              disabled={isEditing}
              onChange={(e) => updateFormData("datosLaborales", e)}
            >
              {sectores?.map((sec) => (
                <MenuItem key={sec.id_sector} value={sec.id_sector}>
                  {sec.nombre_sector}
                </MenuItem>
              ))}
            </Select>
            {errors?.sector_id && (
              <FormHelperText>{errors?.sector_id}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Autocomplete
            fullWidth
            disabled={isEditing}
            options={puestos || []}
            getOptionKey={(option) => option.id}
            getOptionLabel={(option) => option.puesto || ""}
            value={puestos?.find((p) => p.id === datos?.puesto_id) || null}
            onChange={(event, newValue) => {
              updateFormData("datosLaborales", {
                target: {
                  name: "puesto_id",
                  value: newValue ? newValue.id : null,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Puesto"
                variant="standard"
                size="small"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                error={!!errors?.puesto_id}
                helperText={errors?.puesto_id}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Autocomplete
            fullWidth
            disabled={isEditing || !datos.sucursal_id}
            options={categorias || []}
            getOptionKey={(option) => option.id_categoria ?? option.id}
            getOptionLabel={(option) => option.nombre || ""}
            value={
              categorias?.find(
                (c) => (c.id_categoria ?? c.id) === datos?.categoria_id,
              ) || null
            }
            onChange={(event, newValue) => {
              const id = newValue
                ? (newValue.id_categoria ?? newValue.id)
                : null;
              updateFormData("datosLaborales", {
                target: { name: "categoria_id", value: id },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categoría"
                variant="standard"
                size="small"
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Autocomplete
            fullWidth
            disabled={isEditing || !datos.sucursal_id}
            options={tareas || []}
            getOptionKey={(option) => option.id_tarea}
            getOptionLabel={(option) => option.nombre || ""}
            value={tareas?.find((t) => t.id_tarea === datos?.tarea_id) || null}
            onChange={(event, newValue) => {
              updateFormData("datosLaborales", {
                target: {
                  name: "tarea_id",
                  value: newValue ? newValue.id_tarea : null,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tarea"
                variant="standard"
                size="small"
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <FormControl fullWidth variant="standard" size="small" margin="dense">
            <InputLabel shrink id="contratacion-label">
              Contratación / Convenio
            </InputLabel>
            <Select
              labelId="contratacion-label"
              id="convenio_id"
              name="convenio_id"
              value={datos.convenio_id}
              label="Contratación / Convenio"
              disabled={isEditing || !datos.sucursal_id}
              onChange={(e) => updateFormData("datosLaborales", e)}
            >
              {Array.isArray(convenios) &&
                convenios?.map((c, index) => (
                  <MenuItem key={`${c.id}-${index}`} value={c.id}>
                    {c.nombre}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <FormControl fullWidth variant="standard" size="small" margin="dense">
            <InputLabel shrink id="sindicato-label">
              Sindicato
            </InputLabel>
            <Select
              labelId="sindicato-label"
              id="sindicato_id"
              name="sindicato_id"
              label="Sindicato"
              value={datos.sindicato_id}
              disabled={isEditing}
              onChange={(e) => updateFormData("datosLaborales", e)}
            >
              {sindicatos?.map((s) => (
                <MenuItem key={s.id_gremial} value={s.id_gremial}>
                  {s.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Autocomplete
            fullWidth
            disabled={isEditing || !datos.sucursal_id}
            options={obrasSociales || []}
            getOptionKey={(option) => option.id_obra_social}
            getOptionLabel={(option) => option.nombre || ""}
            value={
              obrasSociales?.find(
                (os) => os.id_obra_social === datos?.obra_social_id,
              ) || null
            }
            onChange={(event, newValue) => {
              updateFormData("datosLaborales", {
                target: {
                  name: "obra_social_id",
                  value: newValue ? newValue.id_obra_social : null,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Obra Social"
                variant="standard"
                size="small"
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <TextField
            fullWidth
            variant="standard"
            size="small"
            margin="dense"
            label="Nro. Afiliado O.S."
            name="nro_afiliado_os"
            value={datos.nro_afiliado_os}
            disabled={isEditing}
            onChange={(e) => updateFormData("datosLaborales", e)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Autocomplete
            fullWidth
            disabled={isEditing}
            options={bancos || []}
            getOptionKey={(option) => option.id}
            getOptionLabel={(option) => option.nombre || ""}
            value={bancos?.find((b) => b.id === datos?.banco_id) || null}
            onChange={(event, newValue) => {
              updateFormData("datosLaborales", {
                target: {
                  name: "banco_id",
                  value: newValue ? newValue.id : null,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Banco"
                variant="standard"
                size="small"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                error={!!errors?.banco_id}
                helperText={errors?.banco_id}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <TextField
            fullWidth
            variant="standard"
            size="small"
            margin="dense"
            label="Nro. Cuenta"
            name="nro_cuenta"
            value={datos.nro_cuenta}
            disabled={isEditing}
            error={!!errors?.nro_cuenta}
            helperText={errors?.nro_cuenta}
            onChange={(e) => updateFormData("datosLaborales", e)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <TextField
            fullWidth
            variant="standard"
            size="small"
            margin="dense"
            label="CBU"
            name="cbu"
            value={datos.cbu}
            disabled={isEditing}
            error={!!errors?.cbu}
            helperText={errors?.cbu}
            onChange={(e) => updateFormData("datosLaborales", e)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={2}>
          <TextField
            fullWidth
            variant="standard"
            size="small"
            margin="dense"
            label="Alias"
            name="alias"
            value={datos.alias}
            disabled={isEditing}
            error={!!errors?.alias}
            helperText={errors?.alias}
            onChange={(e) => updateFormData("datosLaborales", e)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={2}>
          <TextField
            fullWidth
            variant="standard"
            size="small"
            margin="dense"
            label="Nro. Chofer"
            name="chofer_nro"
            value={datos.chofer_nro}
            disabled={isEditing}
            onChange={(e) => updateFormData("datosLaborales", e)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2}>
          <TextField
            fullWidth
            variant="standard"
            size="small"
            margin="dense"
            label="Antigüedad Anterior"
            name="antiguedad_anterior"
            value={datos.antiguedad_anterior}
            disabled={isEditing}
            onChange={(e) => updateFormData("datosLaborales", e)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2}>
          <TextField
            fullWidth
            variant="standard"
            size="small"
            margin="dense"
            label="Antigüedad Actual"
            value={antiguedadCalculada}
            InputProps={{
              readOnly: true,
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
        </Grid>

        <Grid item xs={6} sm={4} md={2} lg={2}>
          <FormControlLabel
            control={
              <Checkbox
                name="controlhorario"
                size="small"
                checked={!!datos.controlhorario}
                onChange={(e) => updateFormData("datosLaborales", e)}
                disabled={isEditing}
              />
            }
            label="Control Horario"
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2} lg={2}>
          <FormControlLabel
            control={
              <Checkbox
                name="liquida_ganancias"
                size="small"
                checked={!!datos.liquida_ganancias}
                onChange={(e) => updateFormData("datosLaborales", e)}
                disabled={isEditing}
              />
            }
            label="Liq. Ganancias"
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2} lg={2}>
          <FormControlLabel
            control={
              <Checkbox
                name="presentismo"
                size="small"
                checked={!!datos.presentismo}
                onChange={(e) => updateFormData("datosLaborales", e)}
                disabled={isEditing}
              />
            }
            label="Presentismo"
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2} lg={2}>
          <FormControlLabel
            control={
              <Checkbox
                name="paga_sindical"
                size="small"
                checked={!!datos.paga_sindical}
                onChange={(e) => updateFormData("datosLaborales", e)}
                disabled={isEditing}
              />
            }
            label="Paga Sindicato"
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2} lg={2}>
          <FormControlLabel
            control={
              <Checkbox
                name="liqui_salario_familiar"
                size="small"
                checked={!!datos.liqui_salario_familiar}
                onChange={(e) => updateFormData("datosLaborales", e)}
                disabled={isEditing}
              />
            }
            label="Liq. Sal. Familiar"
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2} lg={2}>
          <FormControlLabel
            control={
              <Checkbox
                name="fuera_convenio"
                size="small"
                checked={!!datos.fuera_convenio}
                onChange={(e) => updateFormData("datosLaborales", e)}
                disabled={isEditing}
              />
            }
            label="Fuera de Convenio"
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {archivos && archivos.length > 0 ? (
          archivos.map((archivo) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={archivo.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  position: "relative",
                  bgcolor: "rgba(0,0,0,0.01)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  title={archivo.nombre}
                  sx={{ fontWeight: 600, pr: 4 }}
                >
                  {archivo.nombre}
                </Typography>

                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleViewFile(archivo)}
                    disabled={
                      !["application/pdf", "image/jpeg", "image/png"].includes(
                        archivo.extension,
                      )
                    }
                    title="Ver"
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() =>
                      DownloadFiles(`${archivo.id_archivo}-${archivo.nombre}`)
                    }
                    title="Descargar"
                  >
                    <Download fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={isEditing}
                    onClick={() => handleDeleteFile(archivo)}
                    title="Eliminar"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No hay archivos adjuntos.
            </Typography>
          </Grid>
        )}

        <Grid item xs={12} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            component="label"
            startIcon={<AddCircle />}
            disabled={isUploading || !uuid_archivo || isEditing}
            size="small"
          >
            Subir Archivo
            <input
              hidden
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileUpload}
            />
          </Button>
          {isUploading && (
            <Typography variant="caption" sx={{ ml: 2, color: "primary.main" }}>
              Subiendo...
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  );
}
